import {testIdProps} from '@github-ui/test-id-props'
import type {PropsWithChildren} from 'react'
import {Box, type SxProp, useFocusZone} from '@primer/react'
import {FocusKeys} from '@primer/behaviors'
import {VisibleItems} from './VisibleItems'
import {OverflowMenu, type OverflowMenuProps} from './OverflowMenu'
import {useActionBarRef} from './ActionBarRefContext'
import {useActionBarResize} from './ActionBarResizeContext'
import {useActionBarContent} from './ActionBarContentContext'

export type VisibleAndOverflowContainerProps = PropsWithChildren<{
  overflowMenuToggleProps?: OverflowMenuProps['anchorProps']
  outerContainerSx?: SxProp['sx']
}> &
  SxProp

const defaultStyles = {
  position: 'relative',
  minWidth: 0,
  display: 'flex',
  overflow: 'visible',
  flexDirection: 'row',
  marginLeft: 'auto',
  alignItems: 'center',
  boxSizing: 'content-box',
  flexShrink: 1,
  flexGrow: 1,
}

export const VisibleAndOverflowContainer = ({
  overflowMenuToggleProps,
  children,
  sx: visibleItemsSx,
  outerContainerSx,
}: VisibleAndOverflowContainerProps) => {
  const {gap, justifySpaceBetween} = useActionBarResize()
  const {outerContainerRef} = useActionBarRef()
  const {label, variant} = useActionBarContent()

  useFocusZone(
    {
      containerRef: outerContainerRef,
      bindKeys: FocusKeys.ArrowHorizontal | FocusKeys.HomeAndEnd,
      focusOutBehavior: 'wrap',
      disabled: variant !== 'toolbar',
    },
    [outerContainerRef],
  )

  return (
    <Box
      ref={outerContainerRef}
      {...testIdProps('action-bar-container')}
      role={variant === 'toolbar' ? 'toolbar' : undefined}
      aria-label={variant === 'toolbar' ? label : undefined}
      sx={{
        ...defaultStyles,
        ...outerContainerSx,
        justifyContent: justifySpaceBetween ? 'space-between' : 'flex-end',
        gap,
      }}
    >
      <VisibleItems sx={visibleItemsSx} />
      {children}
      <OverflowMenu anchorProps={overflowMenuToggleProps} />
    </Box>
  )
}

try{ VisibleAndOverflowContainer.displayName ||= 'VisibleAndOverflowContainer' } catch {}